import React from 'react';
import clsx from 'clsx';
import {AxisTypeValue} from 'highcharts';
import capitalize from 'lodash/capitalize';
import cloneDeep from 'lodash.clonedeep';

import {InputReact} from 'common/components/input';
import {
  AnalysisType,
  AxisUnitPrecision,
  BehaviorMetric,
  BookmarkXAxisOptions,
  BookmarkYAxisOptions,
  InsightsBookmarkParams,
  ShowClause,
} from 'common/report/insights/util/types';
import {ToggleReact} from 'common/components/toggle';
import {AxisAssignment} from 'common/charts/types';
import {ToggleSize, ToggleTheme} from 'common/components/toggle/types';
import {BehaviorShowClause} from 'common/report/insights/models/show-clause';
import {isBehaviorShowClause, isFormulaShowClause} from 'common/report/insights/models/utils';
import {InputSize} from 'common/components/input/types';
import {SvgIconReact} from 'common/components/svg-icon';
import {MetricType} from 'common/query-builder/query-block/constants';
import {ICON_MAPPING} from 'common/query-builder/query-entry-section/util/entry-icon';
import {getAlphaLabelFromIndex} from 'common/util';
import {ButtonReact} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';
import {eventDefinitionsStore} from 'common/report/stores/event-definitions';
import {useMetrics} from 'common/contexts/react/metrics-resource-context';
import {MetricsResource} from 'common/report/model-resources/metrics';
import {BookmarkType} from 'common/report/queries/app-api/bookmark-client';
import {getMetricEntryBehavior} from 'common/report/insights/util/events';

import {getYAxisDefaultOptions} from '../utils';
import styles from './index.module.styl';

export type ChartCustomizationUpdate = {
  primaryYAxis: BookmarkYAxisOptions;
  secondaryYAxis: BookmarkYAxisOptions;
  xAxis?: BookmarkXAxisOptions;
  newShowClauses?: ShowClause[];
};

export type ChartCustomizationAxisProps = {
  bookmarkParams: InsightsBookmarkParams;
  bookmarkType: BookmarkType;
  onUpdate: (update: ChartCustomizationUpdate) => void;
};

enum AxisVisibility {
  Visible = `visible`,
  Hidden = `hidden`,
}

const displayValue = (value: number | undefined) => (value === undefined ? `` : String(value));

const getHeaderRow = (label: string) => {
  return <h1 className={clsx(styles.ccAxisLabel)}>{label}</h1>;
};

const getEventName = (
  clauseType: MetricType,
  clauseName: string,
  clauseId: number,
  metricsContext: MetricsResource,
) => {
  if (clauseType === MetricType.Formula) {
    return clauseName || metricsContext.getOne(clauseId)?.value?.name;
  }
  const eventDefinition = eventDefinitionsStore.value?.[clauseName];
  return eventDefinition?.displayName || eventDefinition?.name || clauseName || clauseType;
};

const getVisibilityRow = (
  axisSetting: BookmarkYAxisOptions,
  setAxisSetting: (setting: Partial<BookmarkYAxisOptions>) => void,
) => {
  return (
    <div className={clsx(styles.row)}>
      <div style={{gridColumn: 1}}>
        <label className={clsx(styles.ccSublabel)}>Axis</label>
      </div>
      <div className={clsx(styles.singleInputColumn)}>
        <ToggleReact
          className={clsx(styles.singleInput)}
          size={ToggleSize.Medium}
          theme={ToggleTheme.SegmentedController}
          options={[
            {label: capitalize(AxisVisibility.Visible), value: AxisVisibility.Visible},
            {label: capitalize(AxisVisibility.Hidden), value: AxisVisibility.Hidden},
          ]}
          selected={axisSetting.labels?.enabled === false ? AxisVisibility.Hidden : AxisVisibility.Visible}
          onSelect={(ev) => {
            setAxisSetting({labels: {enabled: ev.detail.selected === AxisVisibility.Visible}});
          }}
        />
      </div>
    </div>
  );
};

const getLabelRow = (
  axisSetting: BookmarkYAxisOptions,
  setAxisSetting: (setting: Partial<BookmarkXAxisOptions>) => void,
) => {
  return (
    <div className={clsx(styles.row)}>
      <div style={{gridColumn: 1}}>
        <label className={clsx(styles.ccSublabel)}>Label</label>
      </div>
      <div className={clsx(styles.singleInputColumn)}>
        <InputReact
          ariaLabel='Label'
          className={clsx(styles.singleInput)}
          size={InputSize.Medium}
          placeholder={`Unique`}
          value={axisSetting.title?.text || ``}
          onChange={(ev) => setAxisSetting({title: {text: ev.detail.value || null}})}
        />
      </div>
    </div>
  );
};

const getDecimalRowLabel = (precision: number | undefined, leftArrow: boolean) => {
  if (precision === undefined && leftArrow) {
    return `← Default`;
  }
  return `${leftArrow ? `← ` : ``}${(precision === 0 ? `0` : `.`).padEnd(precision + 1, `0`)}${
    leftArrow === false ? ` →` : ``
  }`;
};

const getDecimalOption = (
  precision: number | undefined,
  setAxisSetting: (setting: Partial<BookmarkYAxisOptions>) => void,
  isLeft: boolean,
) => {
  if ((precision === undefined && isLeft) || (precision === 8 && !isLeft)) {
    return null;
  }

  let newPrecision = undefined;
  if (isLeft) {
    newPrecision = precision === 0 ? undefined : precision - 1;
  } else {
    newPrecision = precision === undefined ? 0 : precision + 1;
  }
  return (
    <ButtonReact
      className={clsx(styles.singleInput)}
      theme={ButtonTheme.Secondary}
      onClick={() => setAxisSetting({axisUnit: {precision: newPrecision as AxisUnitPrecision}})}
    >
      {getDecimalRowLabel(newPrecision, isLeft)}
    </ButtonReact>
  );
};

const getDecimalRow = (
  axisSetting: BookmarkYAxisOptions,
  setAxisSetting: (setting: Partial<BookmarkYAxisOptions>) => void,
) => {
  const precision = axisSetting?.axisUnit?.precision;
  return (
    <div className={clsx(styles.row)}>
      <div style={{gridColumn: 1}}>
        <label className={clsx(styles.ccSublabel)}>Decimals</label>
      </div>
      <div style={{gridColumn: 2}}>{getDecimalOption(precision, setAxisSetting, true)}</div>
      <div style={{gridColumn: 3}}>{getDecimalOption(precision, setAxisSetting, false)}</div>
    </div>
  );
};

const getAxisUnitRow = (
  axisSetting: BookmarkYAxisOptions,
  setAxisSetting: (setting: Partial<BookmarkYAxisOptions>) => void,
  defaultPrefixValue?: string,
  defaultSuffixValue?: string,
) => {
  return (
    <div className={clsx(styles.row)}>
      <div style={{gridColumn: 1}}>
        <label className={clsx(styles.ccSublabel)}>Axis Unit</label>
      </div>
      <div style={{gridColumn: 2}}>
        <InputReact
          ariaLabel='Prefix'
          className={clsx(styles.twoInputColumn)}
          size={InputSize.Medium}
          placeholder={defaultPrefixValue || `Prefix`}
          value={axisSetting.axisUnit?.prefix || ``}
          onChange={(ev) =>
            setAxisSetting({axisUnit: {prefix: ev.detail.value || undefined, suffix: axisSetting.axisUnit?.suffix}})
          }
        />
      </div>
      <div style={{gridColumn: 3}}>
        <InputReact
          ariaLabel='Suffix'
          className={clsx(styles.twoInputColumn)}
          size={InputSize.Medium}
          placeholder={defaultSuffixValue || `Suffix`}
          value={axisSetting.axisUnit?.suffix || ``}
          onChange={(ev) =>
            setAxisSetting({axisUnit: {suffix: ev.detail.value || undefined, prefix: axisSetting.axisUnit?.prefix}})
          }
        />
      </div>
    </div>
  );
};

const getScaleRow = (
  axisSetting: BookmarkYAxisOptions,
  setAxisSetting: (setting: Partial<BookmarkYAxisOptions>) => void,
) => {
  return (
    <div className={clsx(styles.row)}>
      <div style={{gridColumn: 1}}>
        <label className={clsx(styles.ccSublabel)}>Scale</label>
      </div>
      <div style={{gridColumn: `2 / span 2`}}>
        <ToggleReact
          className={clsx(styles.singleInput)}
          size={ToggleSize.Medium}
          theme={ToggleTheme.SegmentedController}
          options={[
            {label: capitalize(AnalysisType.Linear), value: AnalysisType.Linear},
            {label: capitalize(AnalysisType.Logarithmic), value: AnalysisType.Logarithmic},
          ]}
          selected={axisSetting.type || AnalysisType.Linear}
          onSelect={(ev) => {
            setAxisSetting({type: ev.detail.selected as AxisTypeValue});
          }}
        />
      </div>
    </div>
  );
};

const getThresholdRow = (
  axisSetting: BookmarkYAxisOptions,
  setAxisSetting: (setting: Partial<BookmarkYAxisOptions>) => void,
  defaultMinValue?: number,
  defaultMaxValue?: number,
) => {
  const minPlaceholderValue = defaultMinValue !== undefined ? displayValue(defaultMinValue) : undefined;
  const maxPlaceholderValue = defaultMaxValue !== undefined ? displayValue(defaultMaxValue) : undefined;
  return (
    <div className={clsx(styles.row)}>
      <div style={{gridColumn: 1}}>
        <label className={clsx(styles.ccSublabel)}>Threshold</label>
      </div>
      <div style={{gridColumn: 2}}>
        <InputReact
          ariaLabel='Min'
          className={clsx(styles.twoInputColumn)}
          size={InputSize.Medium}
          placeholder={minPlaceholderValue || `Min`}
          value={displayValue(axisSetting.min)}
          onChange={(ev) => {
            if (ev.detail.value === ``) {
              setAxisSetting({min: undefined});
              return;
            }
            const newVal = Number(ev.detail.value);
            if (!isNaN(newVal)) {
              setAxisSetting({min: newVal});
            }
          }}
        />
      </div>
      <div style={{gridColumn: 3}}>
        <InputReact
          ariaLabel='Max'
          className={clsx(styles.twoInputColumn)}
          size={InputSize.Medium}
          placeholder={maxPlaceholderValue || `Max`}
          value={displayValue(axisSetting.max)}
          onChange={(ev) => {
            if (ev.detail.value === ``) {
              setAxisSetting({max: undefined});
              return;
            }
            const newVal = Number(ev.detail.value);
            if (!isNaN(newVal)) {
              setAxisSetting({max: newVal});
            }
          }}
        />
      </div>
    </div>
  );
};

export function ChartAxisCustomization(props: ChartCustomizationAxisProps) {
  const metricsContext = useMetrics();
  const defaultOptions = getYAxisDefaultOptions(props.bookmarkParams, props.bookmarkType);
  const [showClauses, setShowClauses] = React.useState<ShowClause[]>(props.bookmarkParams.sections.show || []);

  const [primaryYSetting, _setPrimaryYSetting] = React.useState<BookmarkYAxisOptions>(
    props.bookmarkParams?.displayOptions?.primaryYAxisOptions || {
      min: undefined,
      max: undefined,
      type: AnalysisType.Linear,
      labels: {enabled: true},
    },
  );

  const [secondaryYSetting, _setSecondaryYSetting] = React.useState<BookmarkYAxisOptions>(
    props.bookmarkParams?.displayOptions?.secondaryYAxisOptions || {
      min: undefined,
      max: undefined,
      type: AnalysisType.Linear,
      labels: {enabled: true},
    },
  );

  const [xSetting, _setXSetting] = React.useState<BookmarkXAxisOptions>(
    props.bookmarkParams?.displayOptions?.xAxisOptions || {
      labels: {enabled: true},
    },
  );

  const setPrimaryYSetting = (setting: Partial<BookmarkYAxisOptions>) => {
    _setPrimaryYSetting((old) => {
      const updated = cloneDeep({...old, ...setting});
      props.onUpdate({
        primaryYAxis: updated,
        secondaryYAxis: secondaryYSetting,
        xAxis: xSetting,
        newShowClauses: showClauses,
      });
      return updated;
    });
  };

  const setSecondaryYSetting = (setting: Partial<BookmarkYAxisOptions>) => {
    _setSecondaryYSetting((old) => {
      const updated = cloneDeep({...old, ...setting});
      props.onUpdate({
        primaryYAxis: primaryYSetting,
        secondaryYAxis: updated,
        xAxis: xSetting,
        newShowClauses: showClauses,
      });
      return updated;
    });
  };

  const setXSetting = (setting: Partial<BookmarkXAxisOptions>) => {
    _setXSetting((old) => {
      const updated = cloneDeep({...old, ...setting});
      props.onUpdate({
        primaryYAxis: primaryYSetting,
        secondaryYAxis: secondaryYSetting,
        xAxis: updated,
        newShowClauses: showClauses,
      });
      return updated;
    });
  };

  const getUpdatedClause = (oldClause: any, axisAssignment: AxisAssignment) => {
    const newClause = cloneDeep(oldClause);
    if (!newClause.display) {
      newClause.display = {axis: AxisAssignment.Primary};
    }
    newClause.display.axis = axisAssignment;
    return newClause;
  };

  const getUpdatedClauses = (
    hasNestedBehaviors: boolean,
    axisAssignment: AxisAssignment,
    idx: number,
    oldClauses: ShowClause[],
  ) => {
    const newClauses = cloneDeep(oldClauses);

    newClauses[idx] = getUpdatedClause(newClauses[idx], axisAssignment);

    if (hasNestedBehaviors) {
      const behaviors = (newClauses[idx] as BehaviorMetric)?.behavior?.behaviors || [];
      for (let i = 0; i < behaviors.length; i++) {
        behaviors[i] = getUpdatedClause(behaviors[i], axisAssignment);
      }
    }
    return newClauses;
  };

  const getClauseRow = (hasNestedBehaviors: boolean, idx: number, clause: ShowClause) => {
    const {clauseType, clauseName, clauseId} = isFormulaShowClause(clause)
      ? {clauseType: MetricType.Formula, clauseName: clause?.formula?.name, clauseId: clause?.formula?.id}
      : {
          clauseType: clause.behavior.type,
          clauseName: getMetricEntryBehavior(clause.behavior).name,
          clauseId: clause.behavior.id,
        };

    const display = clause.display;
    const svgIcon = ICON_MAPPING[clauseType];
    return (
      <div className={clsx(styles.axisAssignmentRow)} key={idx}>
        <div style={{gridColumn: 1}}>
          <div style={{display: `flex`, alignItems: `center`}}>
            <ButtonReact className={clsx(styles.eventPrefix)} theme={ButtonTheme.Tertiary} notFocusable>
              <span className={clsx(styles.prefixText)}>{getAlphaLabelFromIndex(idx)}</span>
            </ButtonReact>
            {svgIcon && <SvgIconReact className={clsx(styles.metricIcon)} icon={svgIcon} />}
            <label>{getEventName(clauseType, clauseName, clauseId, metricsContext)}</label>
          </div>
        </div>
        <ToggleReact
          style={{width: `60px`}}
          size={ToggleSize.Medium}
          theme={ToggleTheme.SegmentedController}
          options={[
            {label: `L`, value: AxisAssignment.Primary},
            {label: `R`, value: AxisAssignment.Secondary},
          ]}
          selected={display?.axis || AxisAssignment.Primary}
          onSelect={(ev) => {
            setShowClauses((oldClauses) => {
              const updatedClauses = getUpdatedClauses(
                hasNestedBehaviors,
                ev.detail.selected as AxisAssignment,
                idx,
                oldClauses,
              );

              // If assigning a metric to an axis that doesn't have any other metrics assigned to it, show the labels
              const allPrimary = !oldClauses.some((x) => x.display?.axis === AxisAssignment.Secondary);
              const allSecondary = !oldClauses.some(
                (x) => x.display?.axis === undefined || x.display?.axis === AxisAssignment.Primary,
              );
              const shouldShowPrimary = primaryYSetting.labels?.enabled === false && allSecondary;
              const shouldShowSecondary = secondaryYSetting.labels?.enabled === false && allPrimary;

              const updatedPrimary = shouldShowPrimary
                ? cloneDeep({...primaryYSetting, ...{labels: {enabled: true}}})
                : primaryYSetting;
              const updatedSecondary = shouldShowSecondary
                ? cloneDeep({...secondaryYSetting, ...{labels: {enabled: true}}})
                : secondaryYSetting;

              if (shouldShowPrimary) {
                _setPrimaryYSetting(updatedPrimary);
              } else if (shouldShowSecondary) {
                _setSecondaryYSetting(updatedSecondary);
              }

              props.onUpdate({
                primaryYAxis: updatedPrimary,
                secondaryYAxis: updatedSecondary,
                xAxis: xSetting,
                newShowClauses: updatedClauses,
              });

              return updatedClauses;
            });
          }}
        />
      </div>
    );
  };

  return (
    <div elref='chartCustomizationAxis'>
      <div elref='leftAxis' className={clsx(styles.axisBlock)} style={{marginTop: `16px`}}>
        {getHeaderRow(`Left Y-Axis`)}
        <div className={styles.axisSettingsBlock}>
          {getVisibilityRow(primaryYSetting, setPrimaryYSetting)}
          {getLabelRow(primaryYSetting, setPrimaryYSetting)}
          {getAxisUnitRow(
            primaryYSetting,
            setPrimaryYSetting,
            defaultOptions.axisUnit?.prefix,
            defaultOptions.axisUnit?.suffix,
          )}
          {getThresholdRow(primaryYSetting, setPrimaryYSetting, defaultOptions.softMin, defaultOptions.softMax)}
          {getDecimalRow(primaryYSetting, setPrimaryYSetting)}
          {getScaleRow(primaryYSetting, setPrimaryYSetting)}
        </div>
      </div>

      <div elref='rightAxis' className={styles.axisBlock}>
        {getHeaderRow(`Right Y-Axis`)}
        <div className={styles.axisSettingsBlock}>
          {getVisibilityRow(secondaryYSetting, setSecondaryYSetting)}
          {getLabelRow(secondaryYSetting, setSecondaryYSetting)}
          {getAxisUnitRow(
            secondaryYSetting,
            setSecondaryYSetting,
            defaultOptions.axisUnit?.prefix,
            defaultOptions.axisUnit?.suffix,
          )}
          {getThresholdRow(secondaryYSetting, setSecondaryYSetting, defaultOptions.softMin, defaultOptions.softMax)}
          {getDecimalRow(secondaryYSetting, setSecondaryYSetting)}
          {getScaleRow(secondaryYSetting, setSecondaryYSetting)}
        </div>
      </div>

      <div className={styles.axisBlock}>
        {getHeaderRow(`X-Axis`)}
        <div className={styles.axisSettingsBlock}>
          {getVisibilityRow(xSetting, setXSetting)}
          {getLabelRow(xSetting, setXSetting)}
        </div>
      </div>

      <div>
        {getHeaderRow(`Axis Assignment`)}
        <div className={styles.axisSettingsBlock}>
          {showClauses.map((clause, clauseIdx) => {
            const behavior = (clause as BehaviorShowClause)?.behavior;
            const hasNestedBehaviors = isBehaviorShowClause(clause) && behavior?.behaviors?.length > 0;
            return getClauseRow(hasNestedBehaviors, clauseIdx, clause);
          })}
        </div>
      </div>
    </div>
  );
}
